import { userManagementService } from "../../../services/userManagement.service";
import { useState, useEffect } from "react";
import { ModuleAdmins } from "../../business-admin/pages/UserManagement/UserManagement.interface";

// New permission structure types
interface LocationPermission {
  location_name?: string;
  permissions: string[];
  id_type: "branch" | "warehouse" | "department";
  location_id: string;
}

interface NewUserPermissions {
  [module: string]: LocationPermission[];
}

// Legacy types (keeping for backward compatibility)
interface BranchPermission {
  branch_name: string;
  permissions: string[];
  granted_by_role: string;
  granted_by_name: string;
  granted_by_email: string;
  granted_at: number;
  last_modified_by_name?: string;
  last_modified_by_email?: string;
  last_modified_at?: number;
}

interface ModulePermissions {
  [branchId: string]: BranchPermission;
}

interface UserPermissions {
  [module: string]: ModulePermissions;
}

// Type for legacy permissions (array of strings)
type LegacyPermissions = string[];

// Union type for all permission formats
type PermissionsFormat =
  | NewUserPermissions
  | UserPermissions
  | LegacyPermissions;

export type {
  LocationPermission,
  NewUserPermissions,
  BranchPermission,
  ModulePermissions,
  UserPermissions,
  PermissionsFormat,
  LegacyPermissions,
};

/**
 * Checks if permissions are in legacy format (array of strings)
 * @param permissions Permissions to check
 * @returns True if permissions are in legacy format, false otherwise
 */
const isLegacyPermissions = (
  permissions: PermissionsFormat
): permissions is LegacyPermissions => {
  return Array.isArray(permissions);
};

/**
 * Checks if permissions are in new format (module -> location array)
 * @param permissions Permissions to check
 * @returns True if permissions are in new format, false otherwise
 */
const isNewPermissions = (
  permissions: PermissionsFormat
): permissions is NewUserPermissions => {
  if (Array.isArray(permissions)) return false;
  if (!permissions || typeof permissions !== "object") return false;

  // Check if at least one module has an array of location permissions
  return Object.values(permissions).some(
    (modulePerms) =>
      Array.isArray(modulePerms) &&
      modulePerms.every(
        (item) =>
          typeof item === "object" &&
          "permissions" in item &&
          "location_id" in item &&
          "id_type" in item
      )
  );
};

/**
 * Extracts all permission strings from the new nested structure
 * @param newPermissions New permissions structure
 * @returns Array of all permission strings
 */
const extractAllNewPermissions = (newPermissions: any): string[] => {
  const allPermissions: string[] = [];

  if (!newPermissions || typeof newPermissions !== "object") {
    return allPermissions;
  }

  Object.values(newPermissions).forEach((modulePermissions: any) => {
    if (modulePermissions && Array.isArray(modulePermissions)) {
      modulePermissions.forEach((locationData: any) => {
        if (
          locationData &&
          locationData.permissions &&
          Array.isArray(locationData.permissions)
        ) {
          allPermissions.push(...locationData.permissions);
        }
      });
    }
  });

  return allPermissions;
};

/**
 * Extracts all permission strings from the old nested structure
 * @param permissions Permissions to extract
 * @returns Array of all permission strings
 */
const extractAllPermissions = (permissions: UserPermissions): string[] => {
  const allPermissions: string[] = [];

  Object.values(permissions).forEach((modulePermissions) => {
    if (modulePermissions && typeof modulePermissions === "object") {
      Object.values(modulePermissions).forEach((branchPermission) => {
        if (
          branchPermission &&
          branchPermission.permissions &&
          Array.isArray(branchPermission.permissions)
        ) {
          allPermissions.push(...branchPermission.permissions);
        }
      });
    }
  });

  return allPermissions;
};

/**
 * Normalizes permissions to legacy format for backward compatibility
 */
const normalizePermissions = (permissions: PermissionsFormat): string[] => {
  if (isLegacyPermissions(permissions)) {
    return permissions;
  }

  if (isNewPermissions(permissions)) {
    return extractAllNewPermissions(permissions);
  }

  return extractAllPermissions(permissions as UserPermissions);
};

// ==========================================
// MAIN PERMISSION CHECKING FUNCTION
// ==========================================

/**
 * Main permission checking function - handles new location-based permission structure
 * @param permissionCode Permission string to check (e.g., "accounting.dashboard.view")
 * @param permissions User permissions in new format
 * @param location_id location ID to check permissions for specific location
 * @param isStoreOwner Whether the user is a store owner (bypasses all permission checks)
 * @returns Boolean indicating if user has the permission
 */
export const hasPermission = (
  permissionCode: string,
  permissions: PermissionsFormat | undefined | null,
  location_id: string | undefined | null,
  isStoreOwner: boolean = false
): boolean => {
  // Store owners have access to everything
  if (isStoreOwner) {
    return true;
  }

  if (!permissionCode || !permissions || !location_id) {
    return false;
  }

  // Handle legacy permissions (array of strings)
  if (isLegacyPermissions(permissions)) {
    return permissions.includes(permissionCode);
  }

  // Handle new permission structure
  if (isNewPermissions(permissions)) {
    // Extract module name from permission code (e.g., "accounting" from "accounting.dashboard.view")
    const permissionParts = permissionCode.split(".");
    if (permissionParts.length < 2) {
      return false;
    }

    const moduleName = permissionParts[0];
    const actualPermission = permissionParts.slice(1).join("."); // "dashboard.view"

    // Get the module permissions
    const modulePermissions = permissions[moduleName];
    if (!modulePermissions || !Array.isArray(modulePermissions)) {
      return false;
    }

    // Check permissions for specific branch if branchId is provided

    const branchPermissions = modulePermissions.find(
      (location) => location.location_id === location_id
    );
    return branchPermissions
      ? branchPermissions.permissions.includes(actualPermission)
      : false;
  }

  // Handle old nested structure (backward compatibility)
  const oldPermissions = permissions as UserPermissions;
  const permissionParts = permissionCode.split(".");
  if (permissionParts.length < 2) {
    return false;
  }

  const moduleName = permissionParts[0];
  const actualPermission = permissionParts.slice(1).join(".");

  const modulePermissions = oldPermissions[moduleName];
  if (!modulePermissions || typeof modulePermissions !== "object") {
    return false;
  }

  const branchPermissions = modulePermissions[location_id];
  return branchPermissions
    ? branchPermissions.permissions.includes(actualPermission)
    : false;
};

// ==========================================
// ADDITIONAL HELPER FUNCTIONS
// ==========================================

/**
 * Gets all permissions for a specific module and branch
 * @param permissions User permissions
 * @param module Module name (e.g., "accounting")
 * @param branchId Branch ID
 * @returns Array of permission strings for the module and branch
 */
export const getModuleBranchPermissions = (
  permissions: PermissionsFormat | undefined | null,
  module: string,
  branchId: string
): string[] => {
  if (!permissions || !module || !branchId) {
    return [];
  }

  if (isLegacyPermissions(permissions)) {
    // For legacy permissions, filter by module prefix
    return permissions.filter((perm) => perm.startsWith(`${module}.`));
  }

  if (isNewPermissions(permissions)) {
    const modulePermissions = permissions[module];
    if (!modulePermissions || !Array.isArray(modulePermissions)) {
      return [];
    }

    const branchPermissions = modulePermissions.find(
      (location) => location.location_id === branchId
    );
    return branchPermissions ? branchPermissions.permissions : [];
  }

  // Handle old nested structure
  const oldPermissions = permissions as UserPermissions;
  const modulePermissions = oldPermissions[module];
  if (!modulePermissions || typeof modulePermissions !== "object") {
    return [];
  }

  const branchPermissions = modulePermissions[branchId];
  return branchPermissions ? branchPermissions.permissions : [];
};

/**
 * Gets all locations/branches where user has permissions for a specific module
 * @param permissions User permissions
 * @param module Module name (e.g., "accounting")
 * @returns Array of location objects with permissions
 */
export const getModuleLocations = (
  permissions: PermissionsFormat | undefined | null,
  module: string
): LocationPermission[] => {
  if (!permissions || !module) {
    return [];
  }

  if (isLegacyPermissions(permissions)) {
    return []; // Legacy format doesn't have location info
  }

  if (isNewPermissions(permissions)) {
    const modulePermissions = permissions[module];
    return modulePermissions && Array.isArray(modulePermissions)
      ? modulePermissions
      : [];
  }

  // Handle old nested structure - convert to new format
  const oldPermissions = permissions as UserPermissions;
  const modulePermissions = oldPermissions[module];
  if (!modulePermissions || typeof modulePermissions !== "object") {
    return [];
  }

  return Object.entries(modulePermissions).map(
    ([branchId, branchPermission]) => ({
      location_name: branchPermission.branch_name,
      permissions: branchPermission.permissions,
      id_type: "branch" as const,
      location_id: branchId,
    })
  );
};

// ==========================================
// MODULE ADMIN VALIDATION FUNCTIONS
// ==========================================

/**
 * Checks if the current user is a module admin for a specific module or any module
 * @param userId Current user's ID
 * @param storeId Store/business ID
 * @param module Optional specific module to check (hr, accounting, pos, inventory)
 * @param isStoreOwner Whether the user is a store owner (bypasses all checks)
 * @returns Promise<boolean> indicating if user is a module admin
 */
export const isModuleAdmin = async (
  userId: string,
  storeId: string,
  module?: keyof ModuleAdmins,
  isStoreOwner: boolean = false
): Promise<boolean> => {
  // Store owners have admin access to all modules
  if (isStoreOwner) {
    return true;
  }

  try {
    const response = await userManagementService.getModuleAdmins(storeId);

    if (!response.success) {
      return false;
    }

    const moduleAdmins = response.data;

    // If checking for a specific module
    if (module) {
      const moduleAdmin = moduleAdmins[module];
      return moduleAdmin?.user_id === userId;
    }

    // Check if user is admin for any module
    const allModules: (keyof ModuleAdmins)[] = [
      "hr",
      "accounting",
      "pos",
      "inventory",
    ];
    return allModules.some((moduleKey) => {
      const moduleAdmin = moduleAdmins[moduleKey];
      return moduleAdmin?.user_id === userId;
    });
  } catch (error) {
    console.error("Error checking module admin status:", error);
    return false;
  }
};

/**
 * Gets the modules that the current user is an admin for
 * @param userId Current user's ID
 * @param storeId Store/business ID
 * @param isStoreOwner Whether the user is a store owner (returns all modules)
 * @returns Promise<string[]> Array of module names user is admin for
 */
export const getUserAdminModules = async (
  userId: string,
  storeId: string,
  isStoreOwner: boolean = false
): Promise<string[]> => {
  // Store owners are admin for all modules
  if (isStoreOwner) {
    return ["hr", "accounting", "pos", "inventory"];
  }

  try {
    const response = await userManagementService.getModuleAdmins(storeId);
    if (!response.success) {
      return [];
    }
    const moduleAdmins = response.data;
    const adminModules: string[] = [];

    const allModules: (keyof ModuleAdmins)[] = [
      "hr",
      "accounting",
      "pos",
      "inventory",
    ];

    allModules.forEach((moduleKey) => {
      const moduleAdmin = moduleAdmins[moduleKey];
      if (moduleAdmin?.user_id === userId) {
        adminModules.push(moduleKey);
      }
    });

    return adminModules;
  } catch (error) {
    console.error("Error getting user admin modules:", error);
    return [];
  }
};

/**
 * Checks if the current user is a module admin using cached data
 * This is useful when you already have the module admins data and don't want to make another API call
 * @param userId Current user's ID
 * @param moduleAdmins Module admins data from API
 * @param module Optional specific module to check (hr, accounting, pos, inventory)
 * @param isStoreOwner Whether the user is a store owner (bypasses all checks)
 * @returns Boolean indicating if user is a module admin
 */
export const isModuleAdminCached = (
  userId: string,
  moduleAdmins: ModuleAdmins,
  module?: keyof ModuleAdmins,
  isStoreOwner: boolean = false
): boolean => {
  // Store owners have admin access to all modules
  if (isStoreOwner) {
    return true;
  }

  // If checking for a specific module
  if (module) {
    const moduleAdmin = moduleAdmins[module];
    return moduleAdmin?.user_id === userId;
  }

  // Check if user is admin for any module
  const allModules: (keyof ModuleAdmins)[] = [
    "hr",
    "accounting",
    "pos",
    "inventory",
  ];
  return allModules.some((moduleKey) => {
    const moduleAdmin = moduleAdmins[moduleKey];
    return moduleAdmin?.user_id === userId;
  });
};

/**
 * Gets the modules that the current user is an admin for using cached data
 * @param userId Current user's ID
 * @param moduleAdmins Module admins data from API
 * @param isStoreOwner Whether the user is a store owner (returns all modules)
 * @returns Array of module names user is admin for
 */
export const getUserAdminModulesCached = (
  userId: string,
  moduleAdmins: ModuleAdmins,
  isStoreOwner: boolean = false
): string[] => {
  // Store owners are admin for all modules
  if (isStoreOwner) {
    return ["hr", "accounting", "pos", "inventory"];
  }

  const adminModules: string[] = [];

  const allModules: (keyof ModuleAdmins)[] = [
    "hr",
    "accounting",
    "pos",
    "inventory",
  ];

  allModules.forEach((moduleKey) => {
    const moduleAdmin = moduleAdmins[moduleKey];
    if (moduleAdmin?.user_id === userId) {
      adminModules.push(moduleKey);
    }
  });

  return adminModules;
};

// ==========================================
// REACT HOOKS FOR MODULE ADMIN VALIDATION
// ==========================================

/**
 * React hook to check if the current user is a module admin
 * @param userId Current user's ID
 * @param storeId Store/business ID
 * @param module Optional specific module to check (hr, accounting, pos, inventory)
 * @param isStoreOwner Whether the user is a store owner (bypasses all checks)
 * @returns Object with isAdmin boolean, loading state, and error state
 */
export const useModuleAdmin = (
  userId: string | undefined,
  storeId: string | undefined,
  module?: keyof ModuleAdmins,
  isStoreOwner: boolean = false
) => {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!userId || !storeId) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const adminStatus = await isModuleAdmin(
          userId,
          storeId,
          module,
          isStoreOwner
        );
        setIsAdmin(adminStatus);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to check admin status"
        );
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [userId, storeId, module, isStoreOwner]);

  return { isAdmin, loading, error };
};

/**
 * React hook to get all modules that the current user is an admin for
 * @param userId Current user's ID
 * @param storeId Store/business ID
 * @param isStoreOwner Whether the user is a store owner (returns all modules)
 * @returns Object with adminModules array, loading state, and error state
 */
export const useUserAdminModules = (
  userId: string | undefined,
  storeId: string | undefined,
  isStoreOwner: boolean = false
) => {
  const [adminModules, setAdminModules] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const getAdminModules = async () => {
      if (!userId || !storeId) {
        setAdminModules([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const modules = await getUserAdminModules(
          userId,
          storeId,
          isStoreOwner
        );
        setAdminModules(modules);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to get admin modules"
        );
        setAdminModules([]);
      } finally {
        setLoading(false);
      }
    };

    getAdminModules();
  }, [userId, storeId, isStoreOwner]);

  return { adminModules, loading, error };
};

/**
 * React hook to get module admins data and provide utility functions
 * @param storeId Store/business ID
 * @param isStoreOwner Whether the user is a store owner (affects utility functions)
 * @returns Object with moduleAdmins data, loading state, error state, and utility functions
 */
export const useModuleAdmins = (
  storeId: string | undefined,
  isStoreOwner: boolean = false
) => {
  const [moduleAdmins, setModuleAdmins] = useState<ModuleAdmins | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchModuleAdmins = async () => {
      if (!storeId) {
        setModuleAdmins(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await userManagementService.getModuleAdmins(storeId);

        if (response.success) {
          setModuleAdmins(response.data);
        } else {
          setError("Failed to fetch module admins");
          setModuleAdmins(null);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch module admins"
        );
        setModuleAdmins(null);
      } finally {
        setLoading(false);
      }
    };

    fetchModuleAdmins();
  }, [storeId]);

  // Utility functions that work with the fetched data
  const isUserAdmin = (userId: string, module?: keyof ModuleAdmins) => {
    if (!moduleAdmins) return false;
    return isModuleAdminCached(userId, moduleAdmins, module, isStoreOwner);
  };

  const getUserModules = (userId: string) => {
    if (!moduleAdmins) return [];
    return getUserAdminModulesCached(userId, moduleAdmins, isStoreOwner);
  };

  return {
    moduleAdmins,
    loading,
    error,
    isUserAdmin,
    getUserModules,
  };
};
